import { answerSet, K_MAX, K_MIN } from '../facts';
import type { Fact } from '../facts';
import { shuffle } from '../rng';
import type { Rng } from '../rng';

/** Правдоподобный промах — результат соседней строки таблицы.
 *  `k ± 1` удерживается в 2…10, иначе «10 × 10» получает 110 —
 *  значение, которого нет ни в одной таблице (раздел 5.4). */
export function nearMiss(fact: Fact, rng: Rng): number {
  const neighbours = [fact.k - 1, fact.k + 1].filter((k) => k >= K_MIN && k <= K_MAX);
  for (const k of shuffle(neighbours, rng)) {
    const value = fact.op === 'mul' ? fact.tile * k : k;
    if (value !== fact.answer) return value;
  }
  throw new Error(`nearMiss: нет соседней строки для ${fact.id}`);
}

/** Четыре уникальных значения: верный ответ, правдоподобный промах и два
 *  случайных из ближней трети множества достижимых ответов.
 *  Принадлежности множеству мало — 81 и 90 к заданию «2 × 3» настоящие
 *  произведения, но отбрасываются по величине не считая (раздел 5.4). */
export function buildChoice4(fact: Fact, pool: readonly Fact[], rng: Rng): number[] {
  if (!pool.some((candidate) => candidate.op === fact.op && candidate.tile === fact.tile)) {
    throw new Error(`buildChoice4: факт ${fact.id} не принадлежит переданному пулу`);
  }
  const miss = nearMiss(fact, rng);
  const candidates = answerSet(pool).filter((v) => v !== fact.answer && v !== miss);
  const byDistance = [...candidates].sort(
    (a, b) => Math.abs(a - fact.answer) - Math.abs(b - fact.answer) || a - b,
  );
  const nearest = byDistance.slice(0, Math.max(2, Math.ceil(byDistance.length / 3)));
  if (nearest.length < 2) throw new Error(`buildChoice4: пул слишком мал для ${fact.id}`);
  const randoms = shuffle(nearest, rng).slice(0, 2);
  return shuffle([fact.answer, miss, ...randoms], rng);
}
